/**
 * FilePreview - Preview de arquivos anexados
 * Exibe preview de arquivos acima do input com opção de remover
 */

import React from 'react';
import type { AttachmentFile } from '../hooks/useFileAttachment';
import { Icon } from '@/shared/icons'
interface FilePreviewProps {
  /** Arquivo para preview */
  file: AttachmentFile;
  /** Callback ao remover arquivo */
  onRemove: (fileId: string) => void;
  /** Callback ao retentar upload */
  onRetry?: (fileId: string) => void;
  /** Formatar tamanho do arquivo */
  formatFileSize: (bytes: number) => string;
}

/**
 * Componente de preview de arquivo individual
 *
 * @example
 * ```tsx
 * <FilePreview
 *   file={attachment}
 *   onRemove={(id) => removeAttachment(id)}
 *   formatFileSize={formatFileSize}
 * />
 * ```
 */
const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  onRemove,
  onRetry,
  formatFileSize,
}) => {
  const isImage = file.file.type.startsWith('image/');
  const isVideo = file.file.type.startsWith('video/');
  const isAudio = file.file.type.startsWith('audio/');

  /**
   * Renderiza ícone baseado no tipo de arquivo
   */
  const renderIcon = () => {
    if (isVideo) return <Icon name="video" className="w-6 h-6" style={{ color: '#a855f7' }} />;
    if (isAudio) return <Icon name="music" className="w-6 h-6" style={{ color: '#ec4899' }} />;
    if (file.file.type === 'application/pdf') return <Icon name="file-text" className="w-6 h-6" style={{ color: 'var(--theme-error-color)' }} />;
    return <Icon name="file" className="w-6 h-6" style={{ color: 'var(--theme-info-color)' }} />;
  };

  /**
   * Renderiza indicador de status
   */
  const renderStatusIndicator = () => {
    switch (file.uploadStatus) {
      case 'uploading':
        return (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <Icon name="loader" className="w-6 h-6 text-white animate-spin mx-auto mb-1" />
              <p className="text-xs text-white font-medium">{file.uploadProgress}%</p>
            </div>
          </div>
        );
      case 'completed':
        return (
          <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
            <Icon name="check-circle" className="w-3 h-3 text-white" />
          </div>
        );
      case 'failed':
        return (
          <div className="absolute inset-0 bg-red-500 bg-opacity-40 flex items-center justify-center rounded-lg">
            <Icon name="alert-circle" className="w-6 h-6 text-white" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative group">
      <div
        className="border rounded-lg overflow-hidden transition-colors"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)'
        }}
      >
        {/* Preview area */}
        <div
          className="relative w-20 h-20 flex items-center justify-center"
          style={{ backgroundColor: 'var(--theme-page-background)' }}
        >
          {isImage && file.preview ? (
            <img
              src={file.preview}
              alt={file.file.name}
              className="w-full h-full object-cover"
            />
          ) : (
            renderIcon()
          )}

          {/* Status overlay */}
          {renderStatusIndicator()}

          {/* Remove button */}
          <button
            onClick={() => onRemove(file.id)}
            className="absolute -top-2 -right-2 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            style={{
              backgroundColor: 'var(--theme-error-color)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            title="Remover arquivo"
          >
            <Icon name="x" className="w-3 h-3" />
          </button>
        </div>

        {/* FiFile info */}
        <div
          className="px-2 py-1.5 border-t"
          style={{
            backgroundColor: 'var(--theme-page-background)',
            borderColor: 'var(--theme-card-border)'
          }}
        >
          <p
            className="text-xs font-medium truncate"
            style={{ color: 'var(--theme-card-text)' }}
            title={file.file.name}
          >
            {file.file.name}
          </p>
          <p
            className="text-xs"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {formatFileSize(file.file.size)}
          </p>
        </div>
      </div>

      {/* Retry button for failed uploads */}
      {file.uploadStatus === 'failed' && onRetry && (
        <button
          onClick={() => onRetry(file.id)}
          className="absolute bottom-0 left-0 right-0 text-white text-xs py-1 transition-colors"
          style={{
            backgroundColor: 'var(--theme-error-color)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          title={file.error || 'Clique para tentar novamente'}
        >
          Tentar novamente
        </button>
      )}

      {/* Upload progress bar */}
      {file.uploadStatus === 'uploading' && (
        <div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{ backgroundColor: 'var(--theme-button-secondary)' }}
        >
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${file.uploadProgress}%`,
              backgroundColor: 'var(--theme-sidebar-active)'
            }}
          />
        </div>
      )}
    </div>
  );
};

FilePreview.displayName = 'FilePreview';

export default FilePreview;
